import { createContext, useContext, useMemo, useCallback } from 'react';

import { Exploration, Metric, Model } from './types';
import { getModelOrThrow } from './model/utils';

interface MetadataContext {
  models: Model[];
  explorations: Exploration[];
  metrics: Metric[];
  getModel: (modelId: string) => Model;
}

const defaultContextValue = Symbol();

const MetadataContext = createContext<MetadataContext | typeof defaultContextValue>(
  defaultContextValue,
);

// Taken from https://github.com/DefinitelyTyped/DefinitelyTyped/pull/24509#issuecomment-1545820830
export const useMetadataContext = () => {
  const context = useContext(MetadataContext);
  if (context === defaultContextValue) {
    throw new Error('useMetadataContext must be used within a MetadataContextProvider');
  }
  return context;
};

interface MetadataContextProviderProps {
  models: Model[];
  explorations: Exploration[];
  metrics: Metric[];
  children: React.ReactNode;
}

// The purpose of this context is to provide customer configuration to downstream components. For example,
// explorations and models.
export const MetadataContextProvider = (props: MetadataContextProviderProps) => {
  const { models, explorations, metrics } = props;

  const getModel = useCallback((modelId: string) => getModelOrThrow(models, modelId), [models]);

  const contextValue = useMemo(
    () => ({
      models,
      explorations,
      metrics,
      getModel,
    }),
    [models, explorations, metrics, getModel],
  );

  return <MetadataContext.Provider value={contextValue}>{props.children}</MetadataContext.Provider>;
};
